﻿import { useEffect, useRef, useState, useCallback } from 'react';
import { compressToWebP } from '@/lib/imageUtils';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Lock, Banknote, CreditCard, FileText, Upload, X } from 'lucide-react';
import { upsertFinancialDataSchema, type UpsertFinancialDataInput, type ApiError } from '@pms/shared';
import { payrollService } from '@/services/payroll.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

function getErrorMessage(e: unknown): string {
  const ax = e as AxiosError<ApiError>;
  return ax.response?.data?.error?.message ?? 'Something went wrong';
}

interface Props {
  userId: string;
  employeeName: string;
  open: boolean;
  onClose: () => void;
}

export function FinancialDataModal({ userId, employeeName, open, onClose }: Props) {
  const queryClient = useQueryClient();

  const { data: existing, isLoading } = useQuery({
    queryKey: ['payroll', 'financial', userId],
    queryFn: () => payrollService.getFinancialData(userId),
    enabled: open,
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<UpsertFinancialDataInput>({
    resolver: zodResolver(upsertFinancialDataSchema),
    defaultValues: { per_hour_rate: 0 },
  });

  const salaryStartDate = watch('salary_cycle_start_date');

  useEffect(() => {
    if (existing) {
      reset({
        per_hour_rate:           parseFloat(existing.per_hour_rate) || 0,
        salary_cycle_start_date: existing.salary_cycle_start_date ?? undefined,
        pan_number:              existing.pan_number ?? undefined,
        aadhar_front_url:        existing.aadhar_front_url ?? undefined,
        aadhar_back_url:         existing.aadhar_back_url ?? undefined,
        aadhar_number:           existing.aadhar_number ?? undefined,
        bank_name:               existing.bank_name ?? undefined,
        bank_account_number:     existing.bank_account_number ?? undefined,
        ifsc_code:               existing.ifsc_code ?? undefined,
        upi_id:                  existing.upi_id ?? undefined,
        name_on_account:         existing.name_on_account ?? undefined,
      });
    }
  }, [existing, reset]);

  const save = useMutation({
    mutationFn: (data: UpsertFinancialDataInput) => payrollService.upsertFinancialData(userId, data),
    onSuccess: () => {
      toast.success('Financial data saved');
      void queryClient.invalidateQueries({ queryKey: ['payroll', 'financial', userId] });
      void queryClient.invalidateQueries({ queryKey: ['payroll', 'salary', userId] });
      void queryClient.invalidateQueries({ queryKey: ['payroll', 'rate-history', userId] });
      onClose();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b pb-3 mb-4">
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-teal-600" />
            <div>
              <DialogTitle className="font-semibold text-base">Financial Data Management</DialogTitle>
              <p className="text-xs text-muted-foreground">
                Manage sensitive financial information for <span className="font-medium">{employeeName}</span>. Accessible only to Super Admins.
              </p>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : (
          <form onSubmit={handleSubmit((d) => save.mutate(d))} className="space-y-5">
            {/* Salary Information */}
            <section className="rounded-lg border p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Banknote className="h-4 w-4 text-teal-600" />
                Salary Information
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="per_hour_rate">Per Hour Rate (₹)</Label>
                  <Input id="per_hour_rate" type="number" step="0.01" min="0" {...register('per_hour_rate')} />
                  {errors.per_hour_rate && <p className="text-xs text-red-500">{errors.per_hour_rate.message}</p>}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="salary_cycle_start_date">Salary Cycle Start Date</Label>
                  <DatePicker id="salary_cycle_start_date" value={salaryStartDate ?? ''} onChange={(v) => setValue('salary_cycle_start_date', v || undefined)} />
                </div>
              </div>
            </section>

            {/* Tax Information */}
            <section className="rounded-lg border p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <FileText className="h-4 w-4 text-teal-600" />
                Tax Information
              </div>
              <div className="space-y-1">
                <Label htmlFor="pan_number">PAN Number</Label>
                <Input id="pan_number" placeholder="e.g., ABCDE1234F" {...register('pan_number')} />
              </div>
            </section>

            {/* Aadhar Card */}
            <section className="rounded-lg border p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <CreditCard className="h-4 w-4 text-teal-600" />
                Aadhar Card
              </div>
              <div className="space-y-1">
                <Label htmlFor="aadhar_number">Aadhar Number</Label>
                <Input
                  id="aadhar_number"
                  inputMode="numeric"
                  maxLength={12}
                  placeholder="12 digits"
                  {...register('aadhar_number')}
                />
                {errors.aadhar_number && <p className="text-xs text-red-500">{errors.aadhar_number.message}</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <ImageDropZone
                  label="Aadhar Front"
                  value={watch('aadhar_front_url') ?? ''}
                  onChange={(url) => setValue('aadhar_front_url', url, { shouldValidate: true })}
                  error={errors.aadhar_front_url?.message}
                />
                <ImageDropZone
                  label="Aadhar Back"
                  value={watch('aadhar_back_url') ?? ''}
                  onChange={(url) => setValue('aadhar_back_url', url, { shouldValidate: true })}
                  error={errors.aadhar_back_url?.message}
                />
              </div>
            </section>

            {/* Bank Details */}
            <section className="rounded-lg border p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Banknote className="h-4 w-4 text-teal-600" />
                Bank Details
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="bank_name">Bank Name</Label>
                  <Input id="bank_name" placeholder="e.g., State Bank of India" {...register('bank_name')} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="name_on_account">Name on Account</Label>
                  <Input id="name_on_account" placeholder="Enter account holder name" {...register('name_on_account')} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="bank_account_number">Account Number</Label>
                  <Input id="bank_account_number" placeholder="Enter bank account number" {...register('bank_account_number')} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="ifsc_code">IFSC Code</Label>
                  <Input id="ifsc_code" placeholder="e.g., SBIN0001234" {...register('ifsc_code')} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="upi_id">UPI ID</Label>
                  <Input id="upi_id" placeholder="e.g., name@upi" {...register('upi_id')} />
                </div>
              </div>
            </section>

            <div className="flex justify-end gap-3 pt-1">
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={isSubmitting || save.isPending}>
                {save.isPending ? 'Saving…' : 'Save Financial Data'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── ImageDropZone ───────────────────────────────────────────────────────────────

interface ImageDropZoneProps {
  label: string;
  value: string;
  onChange: (url: string) => void;
  error?: string;
}

function ImageDropZone({ label, value, onChange, error }: ImageDropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const handleFile = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) return;
    const compressed = await compressToWebP(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result;
      if (typeof result === 'string') onChange(result);
    };
    reader.readAsDataURL(compressed);
  }, [onChange]);

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const isPreview = value && (value.startsWith('data:') || value.startsWith('http'));

  return (
    <div className="space-y-1">
      <p className="text-sm font-medium leading-none">{label}</p>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => !isPreview && inputRef.current?.click()}
        className={`relative flex h-28 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors
          ${dragging ? 'border-teal-500 bg-teal-50' : 'border-border hover:border-teal-400 hover:bg-muted/40'}
          ${isPreview ? 'cursor-default p-0 overflow-hidden' : 'gap-1.5'}`}
      >
        {isPreview ? (
          <>
            <img src={value} alt={label} className="h-full w-full object-cover rounded-lg" />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onChange(''); }}
              className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
            >
              <X className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
              className="absolute bottom-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
            >
              <Upload className="h-3 w-3" />
            </button>
          </>
        ) : (
          <>
            <Upload className="h-5 w-5 text-muted-foreground" />
            <p className="text-xs text-muted-foreground text-center px-2">
              Drop image here or <span className="text-teal-600 font-medium">click to browse</span>
            </p>
          </>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onInputChange} />
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}
